import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Dropdown } from '@ui/components/Dropdown';
import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Tab, Tabs } from '@ui/components/Tabs';

import { PrimaryScoringMethod, TournamentAdditionalScore } from '@features/shared/api/typings';
import { PrivateTeamAdditionalScoreInput } from '@features/tournament/ui/components/TournamentSettings/inputs/PrivateTeamAdditionalScoreInput';
import { useDeduplicateScoreOptions } from '@features/tournament/ui/components/TournamentSettings/inputs/useDeduplicateScoreOptions';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';

const SCORING_METHOD_OPTIONS: Tab<PrimaryScoringMethod>[] = [
  { value: 'MatchPoints', label: 'Матчевые очки' },
  { value: 'ScoreSum', label: 'Сумма очков' },
];

export type ScoreOption =
  | {
      value: TournamentAdditionalScore;
      label: string;
      disabled?: boolean;
    }
  | { value: ''; label: ''; disabled?: boolean };

const additionalScoreOptions: ScoreOption[] = [
  { value: '', label: '' },
  { value: 'Berger', label: 'Коэффициент Бергера' },
  { value: 'ScoreSum', label: 'Сумма очков' },
  // { value: 'PartBerger', label: 'Усеч. Бергера', disabled: true },
  { value: 'Match', label: 'Личная встреча' },
];

const swissAdditionalScoreOptions: ScoreOption[] = [
  { value: '', label: '' },
  { value: 'Buchholz', label: 'Бухгольца' },
  { value: 'TruncatedBuchholz', label: 'Усеч. Бухгольца' },
  { value: 'ScoreSum', label: 'Сумма очков' },
  // { value: 'Black', label: 'Парт. черными' },
  { value: 'Match', label: 'Личная встреча' },
];

export function AdditionalScoreInput() {
  const { control, setValue } = useFormContext<TournamentEditFormValues>();
  const [scores, mode, system, primaryScoringMethod] = useWatch({
    name: ['additionalScoreOrder', 'mode', 'system', 'teamModeSettings.primaryScoringMethod'],
    control,
  });

  const options = (system === 'Swiss' ? swissAdditionalScoreOptions : additionalScoreOptions).map(
    (o) => ({
      ...o,
      disabled:
        o.disabled ||
        (['Private', 'PrivateTeam'].includes(mode) && o.value === 'ScoreSum') ||
        (mode === 'Team' && primaryScoringMethod === o.value),
    }),
  );

  useEffect(() => {
    // Unset disabled options
    scores.forEach((v: TournamentAdditionalScore, k: number) => {
      const option = options.find((o) => o.value === v);
      if (option?.disabled) {
        setValue(
          ('additionalScoreOrder.' + k) as 'additionalScoreOrder.0',
          '' as TournamentAdditionalScore,
        );
        return;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system, mode]);

  useDeduplicateScoreOptions(options, setValue, 'additionalScoreOrder', scores);

  return (
    <>
      {mode === 'Team' && (
        <FormRow>
          <FormLabel>Подсчет результатов</FormLabel>
          <FormInput>
            <Controller
              name="teamModeSettings.primaryScoringMethod"
              control={control}
              defaultValue={'MatchPoints'}
              render={({ field: { value, onChange, disabled } }) => {
                return (
                  <Tabs
                    tabs={SCORING_METHOD_OPTIONS}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                  />
                );
              }}
            />
          </FormInput>
        </FormRow>
      )}
      <FormRow>
        <FormLabel>Доп. коэффициент {mode === 'PrivateTeam' && 'в личный зачет'}</FormLabel>
        <FormInput>
          <Controller
            name="additionalScoreOrder.0"
            control={control}
            render={(field) => (
              <Dropdown
                options={options}
                value={field.field.value}
                onChange={field.field.onChange}
                disabled={field.field.disabled}
              />
            )}
          />
          <Controller
            name="additionalScoreOrder.1"
            control={control}
            render={(field) => (
              <Dropdown
                options={options}
                value={field.field.value}
                onChange={field.field.onChange}
                disabled={field.field.disabled}
              />
            )}
          />
          <Controller
            name="additionalScoreOrder.2"
            control={control}
            render={({ field: { value, onChange, disabled } }) => (
              <Dropdown options={options} value={value} onChange={onChange} disabled={disabled} />
            )}
          />
          {system === 'Swiss' && (
            <Controller
              name="additionalScoreOrder.3"
              control={control}
              render={({ field: { value, onChange, disabled } }) => (
                <Dropdown options={options} value={value} onChange={onChange} disabled={disabled} />
              )}
            />
          )}
        </FormInput>
      </FormRow>

      {mode === 'PrivateTeam' && <PrivateTeamAdditionalScoreInput />}
    </>
  );
}
